import { useEffect, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { ApiError, canRetryAfter } from '../../lib/api';
import {
  createRolloutAssetFolders,
  type CreateRolloutAssetFoldersResult,
} from './api';

// OrganicAssets_AutoFolderCreation_Spec §3.1 / §6.3. Rollout-level "Create
// Meta Asset Folders" confirmation + progress + completion summary.
//
// The server endpoint is a single inline-await call (it creates every folder
// while the request holds Cloud Run CPU — spec §3.1 synchronisation), so the
// client can't stream a live "23/152" counter. We show an indeterminate
// "Creating folders…" spinner during the call, then the result summary.

type Props = {
  rolloutId: string;
  // Pre-computed from the grid rows so the confirmation reads accurately
  // before any server call.
  rowsWithCampaign: number;
  campaignCount: number;
  rowsSkippedNoCampaign: number;
  onClose: () => void;
  // Fired after a successful run so the parent can refetch rows (the new
  // folder URLs landed on them).
  onDone: () => void;
};

type Phase =
  | { kind: 'confirm' }
  | { kind: 'running' }
  | { kind: 'done'; result: CreateRolloutAssetFoldersResult }
  // `retryable: false` = the connection timed out (Cloudflare 524) while the
  // server kept working. Retry is withheld then: a second run can duplicate the
  // Drive folder tree — see createFoldersService.ts's header.
  | { kind: 'error'; message: string; retryable: boolean };

export function CreateFoldersModal({
  rolloutId,
  rowsWithCampaign,
  campaignCount,
  rowsSkippedNoCampaign,
  onClose,
  onDone,
}: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
  const cancelRef = useRef<HTMLButtonElement>(null);
  const busy = phase.kind === 'running';

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const run = async () => {
    setPhase({ kind: 'running' });
    try {
      const result = await createRolloutAssetFolders(rolloutId);
      setPhase({ kind: 'done', result });
      onDone();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Folder creation failed. Try again.';
      setPhase({ kind: 'error', message, retryable: canRetryAfter(err) });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div
        className="w-full max-w-lg rounded-md bg-white shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-folders-title"
      >
        <div className="p-6 space-y-4">
          <h2 id="create-folders-title" className="text-lg font-semibold text-slate-900">
            Create Meta Asset Folders
          </h2>

          {phase.kind === 'confirm' && (
            <>
              <p className="text-sm text-slate-700">
                Create Local + Organic folders for{' '}
                <strong>
                  {rowsWithCampaign} {rowsWithCampaign === 1 ? 'row' : 'rows'}
                </strong>{' '}
                across{' '}
                <strong>
                  {campaignCount} {campaignCount === 1 ? 'Campaign' : 'Campaigns'}
                </strong>
                ?
                {rowsSkippedNoCampaign > 0 && (
                  <>
                    {' '}
                    {rowsSkippedNoCampaign}{' '}
                    {rowsSkippedNoCampaign === 1 ? 'row' : 'rows'} skipped (no Campaign
                    assigned).
                  </>
                )}
              </p>
              <p className="text-xs text-slate-500">
                Existing folder links are preserved — only missing folders are created.
                This can take up to a minute or two for large rollouts; keep this dialog
                open until it finishes.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  ref={cancelRef}
                  type="button"
                  onClick={onClose}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <Button onClick={run}>Create folders</Button>
              </div>
            </>
          )}

          {phase.kind === 'running' && (
            <div className="flex items-center gap-3 py-4 text-sm text-slate-700">
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
              Creating folders… this holds the connection open until every folder is
              created.
            </div>
          )}

          {phase.kind === 'done' && (
            <>
              <p className="text-sm text-slate-800">
                Created <strong>{phase.result.foldersCreated}</strong>{' '}
                {phase.result.foldersCreated === 1 ? 'folder' : 'folders'};{' '}
                <strong>{phase.result.rowsUpdated}</strong>{' '}
                {phase.result.rowsUpdated === 1 ? 'row' : 'rows'} updated with new links.
                {phase.result.rowsSkippedNoCampaign > 0 && (
                  <>
                    {' '}
                    {phase.result.rowsSkippedNoCampaign} skipped (no Campaign assigned).
                  </>
                )}
              </p>
              {phase.result.failures.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p className="font-medium">
                    {phase.result.failures.length}{' '}
                    {phase.result.failures.length === 1 ? 'row' : 'rows'} could not be
                    created:
                  </p>
                  <ul className="mt-1 list-disc pl-4 space-y-0.5">
                    {phase.result.failures.slice(0, 12).map((f) => (
                      <li key={f.accountCampaignId}>
                        {f.locationName}: {f.reason}
                      </li>
                    ))}
                    {phase.result.failures.length > 12 && (
                      <li>…and {phase.result.failures.length - 12} more.</li>
                    )}
                  </ul>
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button onClick={onClose}>Done</Button>
              </div>
            </>
          )}

          {phase.kind === 'error' && (
            <>
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {phase.message}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
                {phase.retryable && <Button onClick={run}>Retry</Button>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
